import { parseISO } from "date-fns";
import { Building2, Home } from "lucide-react";
import { weekDays, ymd, fmt } from "@/lib/dates";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

const DEFAULT = { start: "09:00", end: "17:30", location: "office", notes: "" };

const LocationToggle = ({ value, onChange, disabled, testid }) => (
  <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50" data-testid={testid}>
    {[["office", Building2, "Office"], ["remote", Home, "Remote"]].map(([k, Icon, label]) => (
      <button
        key={k}
        type="button"
        disabled={disabled}
        onClick={() => onChange(k)}
        data-testid={`${testid}-${k}`}
        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-colors ${
          value === k ? (k === "office" ? "bg-teal-700 text-white" : "bg-indigo-600 text-white") : "text-slate-500 hover:text-slate-800"
        }`}
      >
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    ))}
  </div>
);

export const RosterEditor = ({ weekStart, shifts, onChange, disabled, offDays = [] }) => {
  const byDate = Object.fromEntries(shifts.map((s) => [s.date, s]));
  const toggle = (date, on) => onChange(on ? [...shifts, { date, ...DEFAULT }].sort((a, b) => a.date.localeCompare(b.date)) : shifts.filter((s) => s.date !== date));
  const update = (date, field, value) => onChange(shifts.map((s) => (s.date === date ? { ...s, [field]: value } : s)));

  return (
    <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="roster-editor">
      {weekDays(parseISO(weekStart)).map((d) => {
        const date = ymd(d);
        const s = byDate[date];
        const off = offDays.includes(date);
        return (
          <div key={date} className={`flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3 transition-colors ${s ? (s.location === "office" ? "bg-teal-50/40" : "bg-indigo-50/40") : ""}`} data-testid={`roster-day-${date}`}>
            <div className="flex items-center gap-3 lg:w-48 shrink-0">
              <Switch checked={!!s} disabled={disabled} onCheckedChange={(on) => toggle(date, on)} data-testid={`roster-day-toggle-${date}`} />
              <div className="leading-tight">
                <div className="font-bold text-slate-800">{fmt(d, "EEEE")}</div>
                <div className="text-xs text-slate-500">{fmt(d, "d MMM")}{off && <span className="ml-1 text-rose-600 font-bold">· On leave</span>}</div>
              </div>
            </div>
            {s ? (
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <Input type="time" value={s.start} disabled={disabled} onChange={(e) => update(date, "start", e.target.value)} className="w-[120px] h-9" data-testid={`roster-start-${date}`} />
                <span className="text-slate-400">–</span>
                <Input type="time" value={s.end} disabled={disabled} onChange={(e) => update(date, "end", e.target.value)} className="w-[120px] h-9" data-testid={`roster-end-${date}`} />
                <LocationToggle value={s.location} disabled={disabled} onChange={(v) => update(date, "location", v)} testid={`roster-location-${date}`} />
                <Input placeholder="Notes (optional)" value={s.notes || ""} disabled={disabled} onChange={(e) => update(date, "notes", e.target.value)} className="flex-1 min-w-[160px] h-9" data-testid={`roster-notes-${date}`} />
              </div>
            ) : (
              <div className="text-sm text-slate-400">Not working</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const toPayload = (shifts) => shifts.map(({ date, start, end, location, notes }) => ({ date, start, end, location, notes: notes || "" }));
